import { Box, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldDriver, EldEventLine } from "src/models";
import useModelData from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { ColoredIcon } from "src/components/colored-icon";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import { addDaysToDate, today } from "src/utils/date-utils";
import PageFilterLoader, { usePageFilterLoaderData } from "src/components/page-filter-loader";
import useModelSavingData from "src/hooks/use-model-saving-data";
import { enqueueSnackbar } from "notistack";
import { DateTime } from "luxon";
import { useAuthContext } from "src/auth/hooks/use-auth-context";
import { EldUnidetifiedEventLineModal } from "./_eld_unidentified_event_line_modal";


type EldUnidentifiedLineProps = {
  eldUnidentifiedLines?: EldEventLine[],
  onEldUnidentifiedLineSelected: (a: EldEventLine) => void
}

const EldUnidentifiedLines = memo(({ eldUnidentifiedLines, onEldUnidentifiedLineSelected }: EldUnidentifiedLineProps) => {
  const { sortedList, sortData } = useSort(eldUnidentifiedLines);

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["eventType", "locationSource", "location"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Unidentified Logs" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="eventTime" sortData={sortData} >event Time</SortableHeader>
              <SortableHeader sortKey="eventDate" sortData={sortData} >event Date</SortableHeader>
              <SortableHeader sortKey="eventType" sortData={sortData} >event Type</SortableHeader>
              <SortableHeader sortKey="numberId" sortData={sortData} >number Id</SortableHeader>
              <SortableHeader sortKey="engineHours" sortData={sortData} >engine Hours</SortableHeader>
              <SortableHeader sortKey="origin" sortData={sortData} >origin</SortableHeader>
              <SortableHeader sortKey="odometer" sortData={sortData} >odometer</SortableHeader>
              <SortableHeader sortKey="locationSource" sortData={sortData} >location Source</SortableHeader>
              <SortableHeader sortKey="location" sortData={sortData} >location</SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((eldUnidentifiedEventLine, index) => {
              const eldUnidentifiedEventLineEventTime = DateTime.fromISO(eldUnidentifiedEventLine.eventTime).setZone(timezone || 'America/Chicago').toFormat('h:mm:ssa');

              return (
                <TableRow
                  key={index}
                  hover
                  onClick={() => onEldUnidentifiedLineSelected(eldUnidentifiedEventLine)}
                  sx={{ cursor: "pointer" }}
                >

                  <TruxTableCell>{eldUnidentifiedEventLineEventTime}</TruxTableCell>
                  <TruxTableCell>{eldUnidentifiedEventLine.eventDate}</TruxTableCell>
                  <TruxTableCell>{eldUnidentifiedEventLine.eventType}</TruxTableCell>
                  <TruxTableCell>{eldUnidentifiedEventLine.numberId}</TruxTableCell>
                  <TruxTableCell>{eldUnidentifiedEventLine.engineHours}</TruxTableCell>
                  <TruxTableCell>{eldUnidentifiedEventLine.origin}</TruxTableCell>
                  <TruxTableCell>{eldUnidentifiedEventLine.odometer}</TruxTableCell>
                  <TruxTableCell>{eldUnidentifiedEventLine.locationSource}</TruxTableCell>
                  <TruxTableCell>{eldUnidentifiedEventLine.location}</TruxTableCell>

                </TableRow>
            )})}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  eldUnidentifiedLines?: EldEventLine[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();

  const [eldDrivers, setEldDrivers] = useState<EldDriver[]>([]);

  const pageFilterData = usePageFilterLoaderData(
    addDaysToDate(today(), -30),
    today()
  );
  
  const { session } = useAuthContext();

  const organizationId = session?.organization?.id

  const dataLoader = useCallback(() => ({
    eldUnidentifiedLines: !pageFilterData.toDate || !pageFilterData.fromDate
      ? []
      : axios.get(
        formatEndpoint(endpoints.eldUnidentifiedLogs.findAll, {
          fromDate: pageFilterData.fromDate,
          toDate: pageFilterData.toDate,
          organizationId
        })
      ).then(response => response.data),
  }), [organizationId, pageFilterData]);

  useEffect(() => {
    axios.get(endpoints.eldDrivers.findAll).then(response => setEldDrivers(response.data));
  }, [])
  
  const {
    data,
    updateData,
    setData,
    error,
    loading,
    setShouldRefresh
  } = useModelData<PageModel>(dataLoader);

  const [isOpen, setIsOpen] = useState(false);

  const selectedEldUnidentifiedLog = useModelSavingData<EldEventLine>(
    endpoints.eldUnidentifiedLogs.update,
  );


  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);


  const onEldUnidentifiedLineSelected = useCallback((a: EldEventLine) => {
    selectedEldUnidentifiedLog.set({
      ...a
    })
    setIsOpen(true);
  }, [selectedEldUnidentifiedLog]);

  const updateEldEventLineUnidentified = () => {
    if (!selectedEldUnidentifiedLog.model) return;

    axios.put(endpoints.eldUnidentifiedLogs.update as string, {
      unidentifiedEventLineId: selectedEldUnidentifiedLog.model.id,
      eldDriverId: selectedEldUnidentifiedLog.model.eldDriverId,
    }).then(response => {
      if (response){
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            eldUnidentifiedLines: prev.eldUnidentifiedLines?.filter(
              (line) => line.id !== response.data.id
            ),
          };
        });
        enqueueSnackbar("Successfully saved the Eld Unidetified Log");
        setIsOpen(false);
        setShouldRefresh(true);
      }
    }).catch((err) => {
      console.log(err)
    })
};

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.eldUnidentifiedLines?.length !== 0 &&
          <PageHeader title="Unidentified Logs" />
        }
        <PageFilterLoader p={0} pageFilterData={pageFilterData} onRun={() => {
          setShouldRefresh(true);
        }} />

        {data && data?.eldUnidentifiedLines?.length === 0 &&
          <NoneFound title={
            <div>
              <Box textAlign="center">
                <ColoredIcon color="success">
                  <Iconify icon="solar:check-circle-bold" width={44} />
                </ColoredIcon>
              </Box>
              No Unidentified Logs Found
            </div>
          } message={<>Unidentified Logs are used to track activity on ELDs when no Driver has been logged in.<br /> All unidentified events should be assigned to drivers.</>} />
        }

        {data && data?.eldUnidentifiedLines?.length !== 0 && <EldUnidentifiedLines eldUnidentifiedLines={data.eldUnidentifiedLines} onEldUnidentifiedLineSelected={onEldUnidentifiedLineSelected} />}

        {data && <EldUnidetifiedEventLineModal
          selectedEldUnidentifiedLog={selectedEldUnidentifiedLog}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          updateEldEventLineUnidentified={updateEldEventLineUnidentified}
          eldDrivers={eldDrivers}/>
        }
      </DisplayPageData>
    </Container >
  );
}
